import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  searchMetaSelectors,
  watchHistory as watchHistorySelector,
  WatchHistoryRequestActions,
} from '@fullstory/redux';
import { UserDetailsResponse } from '@fullstory/client-api';
import { renderDateNickname } from '@fullstory/util/date';
import { parseFieldName, prettifyCustomField, VarType } from '@fullstory/search-vars';

const { getHiddenVars } = searchMetaSelectors;

type UseUserMetadataProps = { userDetails: UserDetailsResponse | null };

export const useUserMetadata = ({ userDetails }: UseUserMetadataProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const hiddenVars = useSelector(getHiddenVars);
  // get and fetch watch history
  const watchHistory = useSelector(watchHistorySelector);
  useEffect(() => {
    dispatch(WatchHistoryRequestActions.fetchWatchHistory());
  }, [dispatch]);

  const userMetadata = useMemo(() => {
    if (!userDetails) return {};
    const userMetadata: { [key: string]: string } = {};
    try {
      const customFields = JSON.parse(userDetails.UserMetadata);
      for (const unprefixedKey in customFields) {
        const key = `user_${unprefixedKey}`;
        if (hiddenVars.has(key)) continue;

        let val: string = customFields[unprefixedKey];
        const parsedField = parseFieldName(key);
        if (parsedField.varType == VarType.Date) {
          val = renderDateNickname(new Date(val));
        }
        userMetadata[prettifyCustomField(parsedField, false, 40)] = val.toString();
      }
    } catch (e) {
      console.error('Failed to parse metadata', e);
    }

    return userMetadata;
  }, [hiddenVars, userDetails]);

  return { watchHistory, userMetadata };
};
